import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import Reveal from "../components/site/Reveal";
import EstimateBar from "../components/site/EstimateBar";
import { CtaLink, LogoTitle } from "../components/site/Common";
import { CityFaq } from "../components/site/CitySeoBlocks";
import { FinalCta } from "../components/site/HomeSections";
import { PRICE_CITIES, PRICE_PLANS, pricePath, priceContent, quartiersOf, quartierPath } from "../seoPages";
import { slugify, aCity } from "../mock";

function Examples({ c }) {
  return (
    <div className="mt-8 overflow-x-auto rounded-3xl bg-white ring-1 ring-[#e6dcc8]" data-testid="price-examples">
      <table className="w-full min-w-[640px] text-left text-[15px]">
        <thead className="bg-[#13261c] text-[#faf7f0]">
          <tr><th className="p-4">Logement</th><th className="p-4">Revenus bruts / an</th>{PRICE_PLANS.map((p) => <th key={p.id} className="p-4">Il vous reste ({p.name} {p.rate} %)</th>)}</tr>
        </thead>
        <tbody>
          {c.examples.map((e, i) => (
            <tr key={e.label} className="border-t border-[#efe7d6]" data-testid={`price-example-${i}`}>
              <td className="p-4 font-bold text-[#13261c]">{e.label}<span className="block text-xs font-normal text-[#6d7268]">≈ {c.eur(e.nightly)} / nuit</span></td>
              <td className="p-4 font-bold text-[#13261c]">{c.eur(e.annual)}</td>
              {e.nets.map((n) => <td key={n.id} className="p-4 text-[#4a5249]">{c.eur(n.net)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PrixPage() {
  const { slug } = useParams();
  const name = PRICE_CITIES.find((n) => slugify(n) === slug);
  if (!name) return <Navigate to="/tarifs" replace />;
  const c = priceContent(name);
  const qs = quartiersOf(name);
  return (
    <div data-testid="price-page" className="bg-gradient-to-b from-[#f3ede1] to-[#faf7f0]">
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 lg:pt-24 pb-16">
        <Reveal><span className="text-sm font-bold uppercase tracking-[0.2em] text-[#c4703f]">Tarifs {name}</span></Reveal>
        <Reveal delay={80} className="mt-4"><LogoTitle title={`prix conciergerie ${name}`} script="sans frais caché" /></Reveal>
        <Reveal delay={140}><p className="mt-8 max-w-3xl text-lg text-[#4a5249] leading-relaxed" data-testid="price-intro">{c.intro}</p></Reveal>
        <div className="mt-10 grid md:grid-cols-3 gap-4">
          {PRICE_PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 80} className={`rounded-3xl p-7 ${p.id === "serenite" ? "bg-[#13261c] text-[#faf7f0]" : "bg-white ring-1 ring-[#e6dcc8] text-[#13261c]"}`}>
              <div data-testid={`price-plan-${p.id}`}>
                <p className="font-bold">{p.name}</p>
                <p className="mt-2 font-logo font-black text-5xl">{p.rate} %</p>
                <p className={`mt-1 text-sm ${p.id === "serenite" ? "text-white/70" : "text-[#6d7268]"}`}>des revenus locatifs</p>
                <p className={`mt-4 text-[15px] leading-relaxed ${p.id === "serenite" ? "text-white/85" : "text-[#4a5249]"}`}>{p.inclus}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal><p className="mt-6 flex gap-2 text-[15px] text-[#4a5249]"><Check className="w-5 h-5 text-[#c4703f] shrink-0" />{c.menage}</p></Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
        <Reveal><h2 className="font-logo font-black text-3xl text-[#13261c]">Exemples chiffrés {aCity(name)}</h2></Reveal>
        <Reveal delay={80}><p className="mt-4 max-w-3xl text-[17px] text-[#4a5249] leading-relaxed">{c.exampleText}</p></Reveal>
        <Examples c={c} />
        <p className="mt-3 text-xs text-[#6d7268]">{c.disclaimer}</p>
        <Reveal><h2 className="mt-12 font-logo font-black text-2xl text-[#13261c]">Ce qui fait le prix d'une nuit {aCity(name)}</h2></Reveal>
        <div className="mt-4 grid lg:grid-cols-2 gap-8 text-[17px] text-[#4a5249] leading-relaxed" data-testid="price-local">{c.local.map((t) => <p key={t}>{t}</p>)}</div>
      </section>

      <section className="py-16 bg-[#efe7d6]" data-testid="price-simulator">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <h2 className="font-logo font-black text-3xl text-[#13261c]">Simulez votre logement {aCity(name)}</h2>
          <p className="mt-3 text-[#4a5249]">Type de logement, chambres, voyageurs : faites votre simulation en 2 minutes.</p>
          <div className="mt-6"><EstimateBar defaultCity={name} /></div>
        </div>
      </section>

      <CityFaq city={{ name }} base={c.faq} />

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20 flex flex-wrap gap-3">
        <CtaLink to="/rendez-vous" testid="price-cta-rdv">Prendre rendez-vous</CtaLink>
        <CtaLink to={`/villes/${slugify(name)}`} variant="ghost" testid="price-cta-city">Conciergerie {name}</CtaLink>
        {qs.map((q) => <Link key={q.slug} to={quartierPath(q)} className="inline-flex items-center gap-1 rounded-full ring-1 ring-[#e6dcc8] bg-white px-5 py-3 font-semibold text-[#13261c] hover:bg-[#13261c] hover:text-white transition-colors" data-testid={`price-quartier-${q.slug}`}>{q.name}<ArrowUpRight className="w-4 h-4" /></Link>)}
        <span className="w-full text-sm text-[#6d7268] mt-4">Autres villes : {PRICE_CITIES.filter((n) => n !== name).map((n, i) => <React.Fragment key={n}>{i ? " · " : ""}<Link to={pricePath(n)} className="underline hover:text-[#13261c]">Prix {n}</Link></React.Fragment>)}</span>
      </section>
      <FinalCta />
    </div>
  );
}
